import { useState, KeyboardEvent, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Command, CommandGroup, CommandItem } from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { captureError } from "@/lib/sentry";
import { isUniqueViolation } from "@/lib/error-handling";

interface Group {
  id: string;
  group_name: string;
}

interface ContactGroupsProps {
  contactId: string;
}

export function ContactGroups({ contactId }: ContactGroupsProps) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [newGroup, setNewGroup] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [, setLoading] = useState(false);
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchGroups();
  }, [contactId]);

  useEffect(() => {
    // Handle clicks outside suggestions box
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('contact_group_members')
        .select(`
          groups (
            id,
            group_name
          )
        `)
        .eq('contact_id', contactId);

      if (error) throw error;

      console.log('Fetched group data:', data);

      const mappedGroups = data
        ? data.map((item: any) => {
            console.log('Mapping item:', item);
            return {
              id: item.groups?.id,
              group_name: item.groups?.group_name
            };
          })
          .filter(group => {
            console.log('Filtering group:', group);
            return group.id && group.group_name;
          })
        : [];

      console.log('Final mapped groups:', mappedGroups);
      setGroups(mappedGroups);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchSuggestions = async (input: string) => {
    if (!currentWorkspace || !input.trim()) {
      setSuggestions([]);
      return;
    }

    try {
      setLoading(true);
      // Get unique groups from the workspace
      const { data, error } = await supabase
        .from('groups')
        .select('id, group_name')
        .eq('workspace_id', currentWorkspace.id)
        .ilike('group_name', `%${input}%`)
        .limit(5);

      if (error) throw error;

      // Filter out already added groups and get unique values
      const uniqueSuggestions = data
        .filter(group => !groups.some(g => g.id === group.id))
        .map(group => group.group_name)
        .filter(group => group.toLowerCase().includes(input.toLowerCase()));

      setSuggestions(uniqueSuggestions);
      setShowSuggestions(true);
    } finally {
      setLoading(false);
    }
  };

  const addGroup = async (group: string) => {
    const trimmedGroup = group.trim().toLowerCase();
    if (!trimmedGroup || !currentWorkspace?.id) return;

    try {
      // First check if group exists
      let groupId;
      const { data: existingGroup, error: groupError } = await supabase
        .from('groups')
        .select('id')
        .eq('workspace_id', currentWorkspace.id)
        .eq('group_name', trimmedGroup)
        .single();

      if (groupError && groupError.code !== 'PGRST116') {
        throw groupError;
      }

      if (existingGroup) {
        groupId = existingGroup.id;
      } else {
        // Create new group
        const { data: newGroup, error: createError } = await supabase
          .from('groups')
          .insert({
            workspace_id: currentWorkspace.id,
            group_name: trimmedGroup,
            created_by: user?.id || null
          })
          .select()
          .single();

        if (createError) throw createError;
        groupId = newGroup.id;
      }

      // Check if contact is already in this group
      const { data: existingMember, error: memberCheckError } = await supabase
        .from('contact_group_members')
        .select('id')
        .eq('group_id', groupId)
        .eq('contact_id', contactId)
        .single();

      if (memberCheckError && memberCheckError.code !== 'PGRST116') {
        throw memberCheckError;
      }

      if (existingMember) {
        toast({
          description: "Contact is already in this group",
          variant: "destructive",
        });
        return;
      }

      // Add contact to group
      const { error: memberError } = await supabase
        .from('contact_group_members')
        .insert({
          group_id: groupId,
          contact_id: contactId
        });

      if (memberError) throw memberError;

      await fetchGroups();
      setNewGroup('');
      setShowSuggestions(false);
      
      toast({
        description: "Group added successfully",
      });
    } catch (error) {
      console.error('Error adding group:', error);
      if (isUniqueViolation(error)) {
        toast({
          title: "Group already exists",
          description: `A group named "${trimmedGroup}" already exists in this workspace.`,
          variant: "destructive",
        });
        return;
      }
      captureError(error as Error, { feature: 'add-group-to-contact' });
      toast({
        title: "Error",
        description: "Failed to add group",
        variant: "destructive",
      });
    }
  };

  const removeGroup = async (groupId: string) => {
    try {
      const { error } = await supabase
        .from('contact_group_members')
        .delete()
        .eq('contact_id', contactId)
        .eq('group_id', groupId);

      if (error) throw error;

      await fetchGroups();
      
      toast({
        description: "Group removed successfully",
      });
    } catch (error) {
      console.error('Error removing group:', error);
      captureError(error as Error, { feature: 'contacts-contact-groups-remove-group' });
      toast({
        title: "Error",
        description: "Failed to remove group",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (value: string) => {
    setNewGroup(value);
    if (value.trim()) {
      fetchSuggestions(value);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newGroup.trim()) {
      e.preventDefault();
      addGroup(newGroup);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    addGroup(suggestion);
    setShowSuggestions(false);
  };

  return (
    <div>
      <div className="relative">
        <Input
          ref={inputRef}
          name="group-label"
          value={newGroup}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type group name and press Enter"
          className="h-7 text-xs mb-2"
          onFocus={() => newGroup.trim() && setShowSuggestions(true)}
        />

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div ref={suggestionsRef}>
            <Command className="absolute z-50 w-full mt-1">
              <CommandGroup>
                {suggestions.map((suggestion) => (
                  <CommandItem
                    key={suggestion}
                    onSelect={() => handleSuggestionClick(suggestion)}
                    className="text-xs"
                  >
                    {suggestion}
                  </CommandItem>
                ))}
              </CommandGroup>
            </Command>
          </div>
        )}
        
        <div className="flex flex-wrap gap-1.5">
          {groups.map((group) => (
            <Badge
              key={group.id}
              variant="secondary"
              className="text-xs flex items-center gap-1"
            >
              {group.group_name}
              <button
                onClick={() => removeGroup(group.id)}
                className="ml-1 hover:bg-muted rounded-full"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}